import { useState, useEffect, useMemo, lazy, Suspense } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, GraduationCap, Building2, Users, TrendingUp, Search, SlidersHorizontal, X, MessageSquare, Loader2, Mail, Star, Briefcase, Sparkles } from "lucide-react";
import { cn, displayName } from "@/lib/utils";
import { useDevice } from "@/hooks/useDeviceType";
import { PageHeader } from "@/components/PageHeader";
import { TECHNOLOGY_AREA_LABEL_MAP, FUNDING_STATUS_V2, SEEKING_LABEL_TO_ENUM } from "@/lib/constants";
import { getProjectSourceType } from "@/lib/projectSource";

// Lazy load desktop version
const OpportunitiesDesktopPage = lazy(() => import('./OpportunitiesPage.desktop'));
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { api } from "@/lib/api";
import { offlineDataCache } from '@/lib/offlineDataCache';
import { useDismissedRecommendations } from "@/hooks/useDismissedRecommendations";
import { RecommendationCard } from "@/components/RecommendationCard";
import { OfflineDataBanner } from '@/components/OfflineDataBanner';
import { toast } from "sonner";

// Source types
const SOURCE_TYPES = ["NPS", "Military/Gov", "Industry"];

/** Format an enum value to a display label using the known map, falling back to title-casing */
const formatEnumLabel = (value: string): string =>
  value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const formatAreaLabel = (value: string): string =>
  TECHNOLOGY_AREA_LABEL_MAP[value] || formatEnumLabel(value);
const FUNDING_LABEL_MAP: Record<string, string> = Object.fromEntries(
  FUNDING_STATUS_V2.map(({ value, label }) => [value, label])
);
const formatFundingLabel = (value: string): string =>
  FUNDING_LABEL_MAP[value] || formatEnumLabel(value);

// NPS Projects filters - match backend enum values
const PROJECT_STAGES = ["concept", "prototype", "pilot_ready", "deployed"];
const PROJECT_STAGE_LABELS: Record<string, string> = {
  concept: "Concept",
  prototype: "Prototype",
  pilot_ready: "Pilot Ready",
  deployed: "Deployed",
};
const SEEKING_TYPES = [
  "data_access_sharing",
  "education_partnership",
  "funding",
  "pilot_program",
  "prototyping_experimentation",
  "rd_partnership",
  "students_interns",
  "tech_licensing",
  "tech_transfer",
];
const SEEKING_LABELS: Record<string, string> = {
  data_access_sharing: "Data Access/Sharing",
  education_partnership: "Education Partnership",
  funding: "Funding Opportunities",
  pilot_program: "Pilot Programs",
  prototyping_experimentation: "Prototyping & Experimentation",
  rd_partnership: "Research Partnership",
  students_interns: "Students/Interns",
  tech_licensing: "Tech Licensing",
  tech_transfer: "Tech Transfer",
  // Legacy DB values → display labels
  "Data Access/Sharing": "Data Access/Sharing",
  "Education Partnership": "Education Partnership",
  "Funding Opportunities": "Funding Opportunities",
  "Pilot Programs": "Pilot Programs",
  "Prototyping & Experimentation": "Prototyping & Experimentation",
  "Research Partnership": "Research Partnership",
  "Student Interns": "Students/Interns",
  "Tech Licensing": "Tech Licensing",
  "Tech Transfer": "Tech Transfer",
};
// SEEKING_LABEL_TO_ENUM imported from @/lib/constants — single source of truth.

interface Project {
  id: string;
  title: string;
  description?: string;
  category?: string;
  stage?: string;
  fundingStatus?: string;
  researchAreas?: string[];
  seeking?: string[];
  classification?: string;
  department?: string;
  demoSchedule?: string;
  students?: string[];
  keywords?: string[];
  interestedCount?: number;
  piId?: string;
  createdAt: string;
  // Opportunity fields (unified model)
  sponsorOrganization?: string;
  location?: string;
  duration?: string;
  deadline?: string;
  featured?: boolean;
  dodAlignment?: string[];
  dodImpact?: string;
  requirements?: string;
  benefits?: string;
  // POC fields
  pocUserId?: string;
  pocFirstName?: string;
  pocLastName?: string;
  pocEmail?: string;
  pocRank?: string;
  pocIsCheckedIn?: boolean;
  pi?: {
    id: string;
    firstName: string;
    lastName: string;
    organization?: string;
    role?: string;
  };
  partner?: {
    id: string;
    name: string;
    organizationType?: string;
  };
}

interface Recommendation {
  id: string;
  type: string;
  title: string;
  description: string;
  reason: string;
  relevanceScore: number;
  tags: string[];
}

// Combined item — all items are Projects with sourceType derived from category
type CombinedItem = Project & {
  sourceType: 'NPS' | 'Military/Gov' | 'Industry';
};

function OpportunitiesSkeleton() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

export default function OpportunitiesPage() {
  const { isDesktop } = useDevice();

  // Render desktop version for desktop users
  if (isDesktop) {
    return (
      <Suspense fallback={<OpportunitiesSkeleton />}>
        <OpportunitiesDesktopPage />
      </Suspense>
    );
  }

  // Mobile/Tablet version
  return <OpportunitiesMobilePage />;
}

function OpportunitiesMobilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const fromAdmin = location.state?.fromAdmin === true;

  const [projects, setProjects] = useState<Project[]>([]);
  const [opportunities, setOpportunities] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Source type filter
  const [selectedSourceTypes, setSelectedSourceTypes] = useState<string[]>([]);

  // NPS filters
  const [selectedStages, setSelectedStages] = useState<string[]>([]);
  const [selectedFunding, setSelectedFunding] = useState<string[]>([]);
  const [selectedSeeking, setSelectedSeeking] = useState<string[]>([]);

  const [sortBy, setSortBy] = useState("recent");
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  // Favorites state
  const [projectFavorites, setProjectFavorites] = useState<Set<string>>(new Set());
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  // Recommendations state
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const { dismiss, isDismissed } = useDismissedRecommendations('projects');
  const [opportunityRecommendations, setOpportunityRecommendations] = useState<Recommendation[]>([]);
  const { dismiss: dismissOpp, isDismissed: isDismissedOpp } = useDismissedRecommendations('opportunities');

  const toggleCardExpanded = (id: string) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleFavorite = async (id: string, _isProject: boolean) => {
    // All items are projects now (unified model)
    const favorites = projectFavorites;
    const setFavorites = setProjectFavorites;
    const endpoint = `/projects/${id}/bookmark`;

    const isFavorited = favorites.has(id);

    // Optimistic update
    setFavorites(prev => {
      const next = new Set(prev);
      if (isFavorited) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

    try {
      if (isFavorited) {
        await api.delete(endpoint);
      } else {
        await api.post(endpoint);
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      // Revert on error
      setFavorites(prev => {
        const next = new Set(prev);
        if (isFavorited) {
          next.add(id);
        } else {
          next.delete(id);
        }
        return next;
      });
      toast.error('Failed to update favorite');
    }
  };

  const isFavorite = (id: string, _isProject: boolean) => {
    return projectFavorites.has(id);
  };

  const totalFavoritesCount = projectFavorites.size;

  const handleContact = async (e: React.MouseEvent, pocUserId?: string | null) => {
    e.stopPropagation();
    if (!pocUserId) {
      toast.error("POC is not registered in app");
      return;
    }

    try {
      const response = await api.post('/messages/conversations', { participantId: pocUserId });
      const conversation = (response as any).data?.data || (response as any).data;
      if (conversation?.id) {
        navigate(`/messages/${conversation.id}`);
      }
    } catch (error) {
      console.error('Failed to start conversation:', error);
      toast.error("Failed to start conversation");
    }
  };

  useEffect(() => {
    loadData();
    loadFavorites();
    fetchRecommendations();
    fetchOpportunityRecommendations();
  }, []);

  const fetchRecommendations = async () => {
    try {
      const response = await api.post('/recommendations', { type: 'project' });
      const data = (response as any)?.recommendations || [];
      setRecommendations(data);
    } catch (error) {
      console.error('Error loading recommendations:', error);
    }
  };

  const fetchOpportunityRecommendations = async () => {
    try {
      const response = await api.post('/recommendations', { type: 'opportunity' });
      const data = (response as any)?.recommendations || [];
      setOpportunityRecommendations(data);
    } catch (error) {
      console.error('Error loading opportunity recommendations:', error);
    }
  };

  const loadFavorites = async () => {
    try {
      const bookmarksRes = await api.get('/projects/bookmarks');
      const bookmarks = (bookmarksRes as any).data?.data || (bookmarksRes as any).data || [];
      setProjectFavorites(new Set(bookmarks.map((b: any) => b.projectId || b.project_id)));
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  const loadData = async () => {
    try {
      if (projects.length === 0 && opportunities.length === 0) {
        setLoading(true);
      }

      const projectsRes = await api.get('/projects');
      const allProjects: Project[] = (projectsRes as any).data?.data || (projectsRes as any).data || [];

      // Split by partner.organizationType: NPS = no partner / nps partner; everything else = Industry or Military/Gov
      const researchProjects = allProjects.filter(p => getProjectSourceType(p) === 'NPS');
      const opportunityProjects = allProjects.filter(p => getProjectSourceType(p) !== 'NPS');

      setProjects(researchProjects);
      setOpportunities(opportunityProjects);

      const writeCache = () => {
        void offlineDataCache.set('projects', allProjects)
          .catch((e) => console.error('Failed to write projects cache', e));
      };
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(writeCache);
      } else {
        setTimeout(writeCache, 0);
      }
    } catch (error) {
      console.error('Error loading data:', error);

      try {
        const cached = await offlineDataCache.get<Project[]>('projects');
        if (cached?.data) {
          const researchProjects = cached.data.filter(p => getProjectSourceType(p) === 'NPS');
          const opportunityProjects = cached.data.filter(p => getProjectSourceType(p) !== 'NPS');
          setProjects(researchProjects);
          setOpportunities(opportunityProjects);
        }
      } catch {
        // ignore
      }
    } finally {
      setLoading(false);
    }
  };

  const visibleRecommendations = recommendations
    .filter(rec => !isDismissed(rec.id))
    .slice(0, 3);

  const handleRecommendationClick = (rec: Recommendation) => {
    const project = projects.find(p =>
      p.title.toLowerCase() === rec.title.toLowerCase() || p.id === rec.id
    );
    if (project) {
      setExpandedCards(prev => new Set([...prev, project.id]));
      const element = document.getElementById(`project-${project.id}`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const visibleOpportunityRecommendations = opportunityRecommendations
    .filter(rec => !isDismissedOpp(rec.id))
    .slice(0, 3);

  const allVisibleRecommendations = [...visibleRecommendations, ...visibleOpportunityRecommendations].slice(0, 3);

  const handleOpportunityRecommendationClick = (rec: Recommendation) => {
    const opp = opportunities.find(o =>
      o.title.toLowerCase() === rec.title.toLowerCase() || o.id === rec.id
    );
    if (opp) {
      setExpandedCards(prev => new Set([...prev, opp.id]));
      const element = document.getElementById(`opportunity-${opp.id}`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Get active filter count
  const activeFilterCount = useMemo(() => {
    return selectedSourceTypes.length + selectedStages.length + selectedFunding.length + selectedSeeking.length;
  }, [selectedSourceTypes, selectedStages, selectedFunding, selectedSeeking]);

  const filteredProjects = useMemo(() => {
    let filtered = [...projects];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.title?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q) ||
        p.researchAreas?.some((a: string) => a.toLowerCase().includes(q)) ||
        (p.pi ? displayName(p.pi).toLowerCase().includes(q) : false) ||
        p.department?.toLowerCase().includes(q)
      );
    }

    if (selectedStages.length > 0) {
      filtered = filtered.filter(p => p.stage && selectedStages.includes(p.stage));
    }

    // Note: funding_status field doesn't exist in database - removing this filter
    // if (selectedFunding.length > 0) {
    //   filtered = filtered.filter(p => selectedFunding.includes(p.funding_status || ''));
    // }

    if (selectedSeeking.length > 0) {
      filtered = filtered.filter(p => {
        const seekingArray = p.seeking || [];
        return seekingArray.some((s: string) => {
          const normalized = SEEKING_LABEL_TO_ENUM[s] || s;
          return selectedSeeking.includes(normalized);
        });
      });
    }

    return filtered;
  }, [projects, searchQuery, selectedStages, selectedFunding, selectedSeeking]);

  // Filter Military/Government opportunities
  const filteredOpportunities = useMemo(() => {
    let filtered = [...opportunities];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(o =>
        o.title?.toLowerCase().includes(q) ||
        o.description?.toLowerCase().includes(q) ||
        o.sponsorOrganization?.toLowerCase().includes(q)
      );
    }

    // Apply stage filter to opportunities if they have a stage field
    if (selectedStages.length > 0) {
      filtered = filtered.filter(o => {
        const stage = o.stage;
        if (!stage) return false;
        return selectedStages.includes(stage);
      });
    }

    if (selectedSeeking.length > 0) {
      filtered = filtered.filter(o => {
        const seekingArray = o.seeking || [];
        return seekingArray.some((s: string) => {
          const normalized = SEEKING_LABEL_TO_ENUM[s] || s;
          return selectedSeeking.includes(normalized);
        });
      });
    }

    return filtered;
  }, [opportunities, searchQuery, selectedStages, selectedSeeking]);

  const toggleArrayFilter = (
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    value: string
  ) => {
    setter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedSourceTypes([]);
    setSelectedStages([]);
    setSelectedFunding([]);
    setSelectedSeeking([]);
    setShowFavoritesOnly(false);
  };

  // Combined and filtered items
  const filteredItems = useMemo(() => {
    // NPS = projects with no partner / NPS partner; Opportunities split by partner.organizationType
    const npsItems: CombinedItem[] = filteredProjects.map(p => ({ ...p, sourceType: 'NPS' as const }));

    const oppItems: CombinedItem[] = filteredOpportunities.map(o => {
      const source = getProjectSourceType(o);
      // filteredOpportunities is already non-NPS, so source is Industry or Military/Gov
      return { ...o, sourceType: source as 'Industry' | 'Military/Gov' };
    });

    let combined = [...npsItems, ...oppItems];

    // Filter by organization type
    if (selectedSourceTypes.length > 0) {
      combined = combined.filter(item => selectedSourceTypes.includes(item.sourceType));
    }

    // Filter by favorites
    if (showFavoritesOnly) {
      combined = combined.filter(item => projectFavorites.has(item.id));
    }

    // Sort
    if (sortBy === "recent") {
      combined.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    } else if (sortBy === "stage") {
      // Sort by stage - items with stage come first, sorted by PROJECT_STAGES order
      // Items without stage (Military/Gov opportunities) go to the end
      combined.sort((a, b) => {
        const stageA = (a as any).stage;
        const stageB = (b as any).stage;
        const indexA = stageA ? PROJECT_STAGES.indexOf(stageA) : PROJECT_STAGES.length;
        const indexB = stageB ? PROJECT_STAGES.indexOf(stageB) : PROJECT_STAGES.length;
        return indexA - indexB;
      });
    }

    return combined;
  }, [filteredProjects, filteredOpportunities, selectedSourceTypes, selectedSeeking, sortBy, showFavoritesOnly, projectFavorites]);

  const renderFilterPanel = () => {
    return (
      <div className="space-y-4">
        {/* Organization Type Filter */}
        <div>
          <div>
            <Label className="text-xs font-semibold">Organization Type</Label>
            <div className="flex gap-3 mt-2">
              {SOURCE_TYPES.map(type => (
                <div key={type} className="flex items-center space-x-2">
                  <Checkbox
                    id={`source-${type}`}
                    checked={selectedSourceTypes.includes(type)}
                    onCheckedChange={(checked) => {
                      setSelectedSourceTypes(prev =>
                        checked ? [...prev, type] : prev.filter(s => s !== type)
                      );
                    }}
                    className="h-4 w-4"
                  />
                  <Label htmlFor={`source-${type}`} className="text-xs cursor-pointer font-normal">{type}</Label>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {/* NPS Filters */}
          <div>
            <Label className="text-xs font-semibold">Project Stage</Label>
            <div className="space-y-1.5 mt-2">
              {PROJECT_STAGES.map((stage) => (
                <div key={stage} className="flex items-center space-x-2">
                  <Checkbox
                    id={`stage-${stage}`}
                    checked={selectedStages.includes(stage)}
                    onCheckedChange={() => toggleArrayFilter(setSelectedStages, stage)}
                  />
                  <Label htmlFor={`stage-${stage}`} className="text-xs">{PROJECT_STAGE_LABELS[stage]}</Label>
                </div>
              ))}
            </div>
          </div>
          {/* Funding Status Filters - Hidden since field doesn't exist in database */}
          {/* <div>
            <Label className="text-xs font-semibold">Funding Status</Label>
            <div className="space-y-1.5 mt-2">
              {FUNDING_STATUSES.map(status => (
                <div key={status} className="flex items-center space-x-2">
                  <Checkbox
                    id={`funding-${status}`}
                    checked={selectedFunding.includes(status)}
                    onCheckedChange={(checked) => {
                      setSelectedFunding(prev =>
                        checked ? [...prev, status] : prev.filter(s => s !== status)
                      );
                    }}
                    className="h-4 w-4"
                  />
                  <Label htmlFor={`funding-${status}`} className="text-xs cursor-pointer font-normal">{status}</Label>
                </div>
              ))}
            </div>
          </div> */}
          <div>
            <Label className="text-xs font-semibold">Seeking</Label>
            <div className="space-y-1.5 mt-2">
              {SEEKING_TYPES.map(type => (
                <div key={type} className="flex items-center space-x-2">
                  <Checkbox
                    id={`seeking-${type}`}
                    checked={selectedSeeking.includes(type)}
                    onCheckedChange={(checked) => {
                      setSelectedSeeking(prev =>
                        checked ? [...prev, type] : prev.filter(s => s !== type)
                      );
                    }}
                    className="h-4 w-4"
                  />
                  <Label htmlFor={`seeking-${type}`} className="text-xs cursor-pointer font-normal">{SEEKING_LABELS[type] || type}</Label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderActiveFilters = () => {
    const filters: { label: string; onRemove: () => void }[] = [];

    selectedSourceTypes.forEach(s => filters.push({ label: s, onRemove: () => setSelectedSourceTypes(prev => prev.filter(x => x !== s)) }));
    selectedStages.forEach(s => filters.push({ label: PROJECT_STAGE_LABELS[s] || s, onRemove: () => setSelectedStages(prev => prev.filter(x => x !== s)) }));
    selectedFunding.forEach(s => filters.push({ label: s, onRemove: () => setSelectedFunding(prev => prev.filter(x => x !== s)) }));
    selectedSeeking.forEach(s => filters.push({ label: SEEKING_LABELS[s] || s, onRemove: () => setSelectedSeeking(prev => prev.filter(x => x !== s)) }));

    if (filters.length === 0 && !showFavoritesOnly) return null;

    return (
      <div className="flex flex-wrap gap-1.5 items-center">
        {showFavoritesOnly && (
          <Badge variant="default" className="gap-1 text-xs py-1 px-2">
            <Star className="w-3 h-3 fill-current" />
            Favorites
            <button onClick={() => setShowFavoritesOnly(false)}>
              <X className="w-3 h-3 ml-1" />
            </button>
          </Badge>
        )}
        {filters.map((filter, idx) => (
          <Badge key={idx} variant="secondary" className="gap-1 text-xs py-1 px-2 text-muted-foreground">
            {filter.label}
            <button onClick={filter.onRemove}>
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
        <Button
          variant="ghost"
          size="sm"
          onClick={clearFilters}
          className="h-6 text-xs px-2"
        >
          Clear
        </Button>
      </div>
    );
  };

  const isNPSItem = (item: CombinedItem): item is Project & { sourceType: 'NPS' } => {
    return item.sourceType === 'NPS';
  };

  return (
    <div className="min-h-screen bg-gradient-subtle pb-24">
      <PageHeader
        title="Collaborative Opportunities"
        subtitle="Discover Collaboration Projects"
        onBack={() => navigate(fromAdmin ? '/admin' : '/')}
      />

      <main className="px-3 md:px-4 pt-3 md:pt-4 space-y-3 md:space-y-4">
        <OfflineDataBanner />

        {/* Search Input */}
        <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search opportunities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-11 md:h-10 text-base"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9"
                onClick={() => setSearchQuery('')}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Filters Row */}
          <div className="flex items-start justify-between gap-2 md:gap-4">
            <div className="flex-1 min-w-0 space-y-3">
              <div className="flex items-center gap-2">
                <Button
                  variant={showFilters ? "default" : "outline"}
                  onClick={() => setShowFilters(!showFilters)}
                  className={cn(
                    "h-11 md:h-10 text-sm",
                    showFilters
                      ? "bg-primary text-primary-foreground [@media(hover:hover)]:hover:bg-primary/90"
                      : "bg-background border-primary/30 text-foreground [@media(hover:hover)]:hover:bg-primary/10 [@media(hover:hover)]:hover:text-foreground active:bg-primary active:text-primary-foreground"
                  )}
                >
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  Filters
                  {activeFilterCount > 0 && (
                    <Badge variant="default" className={cn(
                      "ml-2 text-xs",
                      showFilters ? "bg-primary-foreground text-primary" : "bg-primary text-primary-foreground"
                    )}>
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>

                <Button
                  variant={showFavoritesOnly ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                  className="gap-2 text-sm h-11 md:h-10"
                >
                  <Star className={cn("w-4 h-4", showFavoritesOnly && "fill-current")} />
                  <span className="hidden sm:inline">Favorites</span>
                  {totalFavoritesCount > 0 && (
                    <Badge variant="secondary" className="ml-1 text-xs text-muted-foreground">{totalFavoritesCount}</Badge>
                  )}
                </Button>

                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[130px] md:w-[150px] h-11 md:h-10 text-sm">
                    <SelectValue placeholder="Sort by..." />
                  </SelectTrigger>
                  <SelectContent className="bg-background z-50">
                    <SelectItem value="recent">Most Recent</SelectItem>
                    <SelectItem value="stage">By Stage</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Expandable Filter Panel */}
              {showFilters && (
                <Card>
                  <CardContent className="pt-4 pb-4">
                    {renderFilterPanel()}
                  </CardContent>
                </Card>
              )}

              {/* Active Filter Badges */}
              {renderActiveFilters()}
            </div>
          </div>

          {/* AI Recommendations */}
          {allVisibleRecommendations.length > 0 && !loading && !showFavoritesOnly && (
            <Card className="p-4 md:p-6 bg-gradient-to-br from-accent/5 to-primary/5 border-accent/30 shadow-md">
              <div className="flex items-center gap-2 mb-3 md:mb-4">
                <Sparkles className="h-5 w-5 text-accent" />
                <h2 className="text-base md:text-lg font-semibold">Recommended for You</h2>
              </div>
              <div className="space-y-2 md:space-y-3">
                {allVisibleRecommendations.map((rec) => {
                  const isOppRec = visibleOpportunityRecommendations.some(r => r.id === rec.id);
                  const matchedProject = projects.find(p =>
                    p.title.toLowerCase() === rec.title.toLowerCase() || p.id === rec.id
                  );
                  const matchedOpp = opportunities.find(o =>
                    o.title.toLowerCase() === rec.title.toLowerCase() || o.id === rec.id
                  );
                  return (
                    <RecommendationCard
                      key={rec.id}
                      title={rec.title}
                      reason={rec.reason}
                      matchScore={rec.relevanceScore / 10}
                      tags={isOppRec ? (matchedOpp?.dodAlignment || rec.tags) : (matchedProject?.researchAreas || rec.tags)}
                      maxTags={2}
                      onClick={() => isOppRec ? handleOpportunityRecommendationClick(rec) : handleRecommendationClick(rec)}
                      onDismiss={() => isOppRec ? dismissOpp(rec.id) : dismiss(rec.id)}
                      compact
                    />
                  );
                })}
              </div>
            </Card>
          )}

          {/* Results count */}
          <div className="text-sm text-muted-foreground">
            Showing {filteredItems.length} opportunities
          </div>

          {/* Unified List */}
          <div className="space-y-4">
            {loading ? (
              <div className="space-y-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Card key={i} className="p-4 md:p-6">
                    <div className="mb-3">
                      <Skeleton className="h-5 w-16 rounded-full" />
                    </div>
                    <div className="space-y-2">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-2/3" />
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Skeleton className="h-6 w-20 rounded-full" />
                      <Skeleton className="h-6 w-24 rounded-full" />
                    </div>
                  </Card>
                ))}
              </div>
            ) : filteredItems.length === 0 ? (
              <Card className="p-12 text-center">
                {showFavoritesOnly ? (
                  <>
                    <Star className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Favorites Yet</h3>
                    <p className="text-muted-foreground mb-6">Mark opportunities as favorites to easily find them later</p>
                    <Button onClick={() => setShowFavoritesOnly(false)} variant="outline">
                      Browse All Opportunities
                    </Button>
                  </>
                ) : (
                  <>
                    <Search className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Opportunities Found</h3>
                    <p className="text-muted-foreground mb-6">Try adjusting your filters or search</p>
                    <Button onClick={clearFilters} variant="outline">
                      Clear Filters
                    </Button>
                  </>
                )}
              </Card>
            ) : (
              filteredItems.map((item) => (
                isNPSItem(item) ? (
                  // NPS Project Card
                  <Collapsible
                    key={`nps-${item.id}`}
                    id={`project-${item.id}`}
                    open={expandedCards.has(item.id)}
                    onOpenChange={() => toggleCardExpanded(item.id)}
                  >
                    <Card className="p-4 md:p-6 hover:shadow-lg transition-shadow">
                      {/* NPS Badge and Star */}
                      <div className="flex items-center justify-between mb-3">
                        <Badge className="bg-blue-600 text-white text-xs">
                          <GraduationCap className="h-3 w-3 mr-1" />
                          NPS
                        </Badge>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="shrink-0 h-9 w-9 [@media(hover:hover)]:hover:bg-transparent active:bg-transparent"
                          onClick={(e) => { e.stopPropagation(); toggleFavorite(item.id, true); }}
                        >
                          <Star className={cn(
                            "w-5 h-5 transition-colors",
                            isFavorite(item.id, true) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground [@media(hover:hover)]:hover:text-yellow-400"
                          )} />
                        </Button>
                      </div>

                      <div className="flex items-start gap-3 md:gap-4 mb-3">
                        <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg md:rounded-xl overflow-hidden bg-blue-500/10 flex items-center justify-center flex-shrink-0 border border-blue-500/20">
                          <GraduationCap className="h-6 w-6 md:h-8 md:w-8 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base md:text-lg font-semibold mb-1 line-clamp-2">{item.title}</h3>
                          <p className="text-sm text-muted-foreground line-clamp-3 whitespace-pre-line">
                            {item.description}
                          </p>
                        </div>
                        <div className="flex flex-col gap-1 ml-3 shrink-0">
                          <Badge variant="outline" className="text-xs border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400">
                            {PROJECT_STAGE_LABELS[item.stage!] || item.stage}
                          </Badge>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {item.researchAreas?.slice(0, 3).map((area: string, idx: number) => (
                          <Badge key={idx} variant="outline" className="text-xs text-muted-foreground">
                            {formatAreaLabel(area)}
                          </Badge>
                        ))}
                        {(item.researchAreas?.length || 0) > 3 && (
                          <Badge variant="outline" className="text-xs text-muted-foreground">
                            +{(item.researchAreas?.length || 0) - 3}
                          </Badge>
                        )}
                      </div>

                      <div className="flex flex-col gap-1 text-sm text-muted-foreground mb-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <Users className="h-4 w-4 shrink-0" />
                            <span className="truncate">
                              {item.pocRank && <span className="font-medium">{item.pocRank}</span>}
                              {item.pocRank && (item.pocFirstName || item.pocLastName) && ' '}
                              {(item.pocFirstName || item.pocLastName)
                                ? `${item.pocFirstName || ''} ${item.pocLastName || ''}`.trim()
                                : (item.pi ? displayName(item.pi) : 'Unknown')}
                            </span>
                            {item.department && (
                              <span className="text-xs hidden md:inline">• {item.department}</span>
                            )}
                          </div>
                          {(item.interestedCount || 0) > 0 && (
                            <div className="flex items-center gap-1 shrink-0">
                              <TrendingUp className="h-4 w-4" />
                              <span>{item.interestedCount}</span>
                            </div>
                          )}
                        </div>
                        {item.pocEmail && (
                          <a
                            href={`mailto:${item.pocEmail}`}
                            className="flex items-center gap-2 text-xs text-primary hover:underline ml-6"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Mail className="h-3 w-3" />
                            {item.pocEmail}
                          </a>
                        )}
                      </div>

                      {/* Expandable Content */}
                      <CollapsibleContent className="space-y-3 pt-3 border-t">
                        {item.description && (
                          <p className="text-sm text-muted-foreground break-words whitespace-pre-line">{item.description}</p>
                        )}

                        {item.dodImpact && (
                          <div>
                            <span className="text-xs text-muted-foreground font-medium">Mission Impact:</span>
                            <p className="text-sm text-muted-foreground break-words whitespace-pre-line">{item.dodImpact}</p>
                          </div>
                        )}

                        {item.seeking && item.seeking.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            <span className="text-xs text-muted-foreground font-medium">Seeking:</span>
                            {item.seeking.map((s: string, idx: number) => (
                              <Badge key={idx} className="text-xs bg-primary/10 text-primary border-0">
                                {SEEKING_LABELS[s] || s}
                              </Badge>
                            ))}
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {item.classification && (
                            <div>
                              <span className="text-muted-foreground font-medium">Classification:</span>
                              <p className="text-foreground">{item.classification}</p>
                            </div>
                          )}

                          {item.fundingStatus && (
                            <div>
                              <span className="text-muted-foreground font-medium">Funding:</span>
                              <p className="text-foreground">{formatFundingLabel(item.fundingStatus!)}</p>
                            </div>
                          )}

                          {item.department && (
                            <div>
                              <span className="text-muted-foreground font-medium">Department:</span>
                              <p className="text-foreground">{item.department}</p>
                            </div>
                          )}

                          {item.demoSchedule && (
                            <div>
                              <span className="text-muted-foreground font-medium">Demo Schedule:</span>
                              <p className="text-foreground">{item.demoSchedule}</p>
                            </div>
                          )}
                        </div>

                        {item.students && item.students.length > 0 && (
                          <div>
                            <span className="text-xs text-muted-foreground font-medium">Team Members:</span>
                            <p className="text-sm text-foreground">{item.students.join(', ')}</p>
                          </div>
                        )}

                        {item.keywords && item.keywords.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            <span className="text-xs text-muted-foreground font-medium">Keywords:</span>
                            {item.keywords.map((k: string, idx: number) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {k}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </CollapsibleContent>

                      {/* Footer with Message button and expand arrow */}
                      <div className="flex items-start gap-2 mt-3">
                        <div className="flex-1 flex flex-col">
                          <Button
                            className={cn(
                              "w-full h-11 md:h-10 text-sm gap-2",
                              item.pocUserId && item.pocIsCheckedIn
                                ? "bg-primary [@media(hover:hover)]:hover:bg-primary/90"
                                : "bg-gray-400 cursor-not-allowed"
                            )}
                            onClick={(e) => handleContact(e, item.pocUserId)}
                            disabled={!(item.pocUserId && item.pocIsCheckedIn)}
                          >
                            <MessageSquare className="h-4 w-4" />
                            Message
                          </Button>
                          {!item.pocUserId ? (
                            <span className="text-[10px] text-muted-foreground mt-1">POC is not registered</span>
                          ) : !item.pocIsCheckedIn ? (
                            <span className="text-[10px] text-amber-600 mt-1">POC registered but not at event</span>
                          ) : null}
                        </div>
                        <CollapsibleTrigger asChild>
                          <Button variant="outline" size="icon" className="h-11 w-11 md:h-10 md:w-10 shrink-0">
                            <ChevronDown className={cn(
                              "h-5 w-5 text-muted-foreground transition-transform duration-200",
                              expandedCards.has(item.id) && "rotate-180"
                            )} />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                    </Card>
                  </Collapsible>
                ) : (
                  // Military/Gov or Industry Card
                  <Collapsible
                    key={`opp-${item.id}`}
                    id={`opportunity-${item.id}`}
                    open={expandedCards.has(item.id)}
                    onOpenChange={() => toggleCardExpanded(item.id)}
                  >
                    <Card className="p-4 md:p-6 hover:shadow-lg transition-shadow">
                      {/* Badge and Star */}
                      <div className="flex items-center justify-between mb-3">
                        {item.sourceType === 'Industry' ? (
                          <Badge variant="outline" className="bg-slate-700 dark:bg-slate-600 text-white text-xs border-slate-700 dark:border-slate-600">
                            <Users className="h-3 w-3 mr-1" />
                            Industry
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-sky-700 dark:bg-sky-600 text-white text-xs border-sky-700 dark:border-sky-600">
                            <Building2 className="h-3 w-3 mr-1" />
                            Military/Gov
                          </Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="shrink-0 h-9 w-9 [@media(hover:hover)]:hover:bg-transparent active:bg-transparent"
                          onClick={(e) => { e.stopPropagation(); toggleFavorite(item.id, true); }}
                        >
                          <Star className={cn(
                            "w-5 h-5 transition-colors",
                            isFavorite(item.id, true) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground [@media(hover:hover)]:hover:text-yellow-400"
                          )} />
                        </Button>
                      </div>

                      <div className="flex items-start gap-3 md:gap-4 mb-3">
                        {item.sourceType === 'Industry' ? (
                          <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg md:rounded-xl overflow-hidden bg-slate-500/10 flex items-center justify-center flex-shrink-0 border border-slate-500/20">
                            <Briefcase className="h-6 w-6 md:h-8 md:w-8 text-slate-700 dark:text-slate-400" />
                          </div>
                        ) : (
                          <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg md:rounded-xl overflow-hidden bg-sky-500/10 flex items-center justify-center flex-shrink-0 border border-sky-500/20">
                            <Building2 className="h-6 w-6 md:h-8 md:w-8 text-sky-700 dark:text-sky-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base md:text-lg font-semibold mb-1 line-clamp-2">{item.title}</h3>
                          <p className="text-sm text-muted-foreground line-clamp-3 whitespace-pre-line">
                            {item.description}
                          </p>
                        </div>
                        <div className="flex flex-col gap-1 ml-3 shrink-0">
                          {item.stage && (
                            <Badge variant="outline" className="text-xs border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400">
                              {PROJECT_STAGE_LABELS[item.stage] || item.stage}
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {(item.dodAlignment || item.researchAreas)?.slice(0, 3).map((area: string, idx: number) => (
                          <Badge key={idx} variant="outline" className="text-xs text-muted-foreground">
                            {formatAreaLabel(area)}
                          </Badge>
                        ))}
                        {((item.dodAlignment || item.researchAreas)?.length || 0) > 3 && (
                          <Badge variant="outline" className="text-xs text-muted-foreground">
                            +{((item.dodAlignment || item.researchAreas)?.length || 0) - 3}
                          </Badge>
                        )}
                      </div>

                      <div className="flex flex-col gap-1 text-sm text-muted-foreground mb-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <Users className="h-4 w-4 shrink-0" />
                            <span className="truncate">
                              {item.pocRank && <span className="font-medium">{item.pocRank}</span>}
                              {item.pocRank && (item.pocFirstName || item.pocLastName) && ' '}
                              {(item.pocFirstName || item.pocLastName)
                                ? `${item.pocFirstName || ''} ${item.pocLastName || ''}`.trim()
                                : 'Unknown'}
                            </span>
                            {(item.department || item.sponsorOrganization) && (
                              <span className="text-xs hidden md:inline">• {item.department || item.sponsorOrganization}</span>
                            )}
                          </div>
                          {(item.interestedCount || 0) > 0 && (
                            <div className="flex items-center gap-1 shrink-0">
                              <TrendingUp className="h-4 w-4" />
                              <span>{item.interestedCount}</span>
                            </div>
                          )}
                        </div>
                        {item.pocEmail && (
                          <a
                            href={`mailto:${item.pocEmail}`}
                            className="flex items-center gap-2 text-xs text-primary hover:underline ml-6"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Mail className="h-3 w-3" />
                            {item.pocEmail}
                          </a>
                        )}
                      </div>

                      {/* Expandable Content */}
                      <CollapsibleContent className="space-y-3 pt-3 border-t">
                        {item.description && (
                          <p className="text-sm text-muted-foreground break-words whitespace-pre-line">{item.description}</p>
                        )}

                        {item.dodImpact && (
                          <div>
                            <span className="text-xs text-muted-foreground font-medium">Mission Impact:</span>
                            <p className="text-sm text-muted-foreground break-words whitespace-pre-line">{item.dodImpact}</p>
                          </div>
                        )}

                        {(item.seeking && item.seeking.length > 0) && (
                          <div className="flex flex-wrap gap-1.5">
                            <span className="text-xs text-muted-foreground font-medium">Seeking:</span>
                            {item.seeking.map((s: string, idx: number) => (
                              <Badge key={idx} className="text-xs bg-primary/10 text-primary border-0">
                                {SEEKING_LABELS[s] || s}
                              </Badge>
                            ))}
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {item.classification && (
                            <div>
                              <span className="text-muted-foreground font-medium">Classification:</span>
                              <p className="text-foreground">{item.classification}</p>
                            </div>
                          )}

                          {item.fundingStatus && (
                            <div>
                              <span className="text-muted-foreground font-medium">Funding:</span>
                              <p className="text-foreground">{formatFundingLabel(item.fundingStatus!)}</p>
                            </div>
                          )}

                          {(item.department || item.sponsorOrganization) && (
                            <div>
                              <span className="text-muted-foreground font-medium">Department:</span>
                              <p className="text-foreground">{item.department || item.sponsorOrganization}</p>
                            </div>
                          )}

                          {(item.demoSchedule || item.duration) && (
                            <div>
                              <span className="text-muted-foreground font-medium">Demo Schedule:</span>
                              <p className="text-foreground">{item.demoSchedule || item.duration}</p>
                            </div>
                          )}
                        </div>

                        {item.students && item.students.length > 0 && (
                          <div>
                            <span className="text-xs text-muted-foreground font-medium">Team Members:</span>
                            <p className="text-sm text-foreground">{item.students.join(', ')}</p>
                          </div>
                        )}

                        {item.keywords && item.keywords.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            <span className="text-xs text-muted-foreground font-medium">Keywords:</span>
                            {item.keywords.map((k: string, idx: number) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {k}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </CollapsibleContent>

                      {/* Footer with Message button and expand arrow */}
                      <div className="flex items-start gap-2 mt-3">
                        <div className="flex-1 flex flex-col">
                          <Button
                            className={cn(
                              "w-full h-11 md:h-10 text-sm gap-2",
                              item.pocUserId && item.pocIsCheckedIn
                                ? "bg-primary [@media(hover:hover)]:hover:bg-primary/90"
                                : "bg-gray-400 cursor-not-allowed"
                            )}
                            onClick={(e) => handleContact(e, item.pocUserId)}
                            disabled={!(item.pocUserId && item.pocIsCheckedIn)}
                          >
                            <MessageSquare className="h-4 w-4" />
                            Message
                          </Button>
                          {!item.pocUserId ? (
                            <span className="text-[10px] text-muted-foreground mt-1">POC is not registered</span>
                          ) : !item.pocIsCheckedIn ? (
                            <span className="text-[10px] text-amber-600 mt-1">POC registered but not at event</span>
                          ) : null}
                        </div>
                        <CollapsibleTrigger asChild>
                          <Button variant="outline" size="icon" className="h-11 w-11 md:h-10 md:w-10 shrink-0">
                            <ChevronDown className={cn(
                              "h-5 w-5 text-muted-foreground transition-transform duration-200",
                              expandedCards.has(item.id) && "rotate-180"
                            )} />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                    </Card>
                  </Collapsible>
                )
              ))
            )}
          </div>
        </main>
    </div>
  );
}
